import { DestroyRef, Inject, Injectable } from '@angular/core';
import {
    AppNotificationService,
    AppNotificationType,
    ConfigConstants,
    DeviceLockService,
    EventService,
    InitializableInterface,
    isDefined,
    SynchronizationService,
    TimeUtilities,
} from '@evorait/pace-core';
import { BehaviorSubject, forkJoin, mergeMap, Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { PhysicalInventoryModel, PhysicalInventoryModelFromJson } from '../models/physical-inventory.model';
import moment from 'moment';
import { PiScheduleDetails } from '../class/pi-schedule';
import { PhysicalInventoryStorageService } from './physical-inventory-storage.service';
import { window } from 'ngx-bootstrap/utils';
import { OdataPhysicalInventoryService } from './odata-physical-inventory.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import PI_ALERT_TYPE = ConfigConstants.PI_ALERT_TYPE;

@Injectable({ providedIn: 'root' })
export class PhysicalInventoryService implements InitializableInterface {
    public isOfflineAlertTimerActive = false;
    private offlineAlertTimerId: number;
    private physicalInventoryTimerId: number | undefined;
    public isInitialized = false;
    private activePIScheduleSubject = new BehaviorSubject<boolean>(false);
    public activePISchedule$: Observable<boolean> = this.activePIScheduleSubject.asObservable();

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private physicalInventoryStorageService: PhysicalInventoryStorageService,
        private readonly eventService: EventService,
        private readonly odataPhysicalInventoryService: OdataPhysicalInventoryService,
        private readonly destroyRef: DestroyRef,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        private readonly synchronizationService: SynchronizationService,
        private readonly deviceLockService: DeviceLockService,
    ) {
        this.eventService.pIAlertUpdate$
            .pipe(
                mergeMap(() => this.physicalInventoryStorageService.getPIScheduleDetails()),
                tap((schedule) => this.checkAndEmitActivePISchedule(schedule)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.deviceLockService.lockReleased$
            .pipe(
                tap(() => this.clearScheduledTimers()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    /**
     * Initializes the PhysicalInventoryService.
     * 1. Gets the PI schedule details from storage.
     * 2. Gets the PI schedule from backend.
     * 3. Sets the activePISchedule$ observable to true if the PI schedule is valid.
     *
     * @returns Observable<void>
     */
    public init(): Observable<void> {
        return forkJoin({
            originalSchedule: this.physicalInventoryStorageService.getPIScheduleDetails().pipe(take(1)),
            backendData: this.odataPhysicalInventoryService.getPiSchedule(),
        }).pipe(
            switchMap(({ originalSchedule, backendData }) => {
                if (!isDefined(backendData)) {
                    return isDefined(originalSchedule) ? of(originalSchedule) : of(undefined);
                }
                // If the backend returns empty, that means there is no pi schedule.
                // So delete any existing/ old data from the DB
                if (backendData.isModelEmpty()) {
                    return isDefined(originalSchedule)
                        ? this.physicalInventoryStorageService
                              .deletePIScheduleDetails()
                              .pipe(mergeMap(() => this.physicalInventoryStorageService.deletePIData()))
                        : of(undefined);
                }
                const startDateAndTime = TimeUtilities.momentFromDateAndTime(
                    backendData.scheduleDate,
                    backendData.startTime,
                    true,
                );
                const endDateAndTime = TimeUtilities.momentFromDateAndTime(
                    backendData.scheduleDate,
                    backendData.endTime,
                    true,
                );

                return this.compareAndUpdateLatestPIScheduleToLocal(originalSchedule, startDateAndTime, endDateAndTime);
            }),
            tap((schedule) => {
                this.isInitialized = true;
                this.checkAndEmitActivePISchedule(schedule);

                if (isDefined(schedule) && schedule.startDateAndTime.isValid()) {
                    this.setPhysicalInventoryTimer(schedule.startDateAndTime);
                }
            }),
            map(() => undefined),
            catchError((error) => {
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('PHYSICAL_INVENTORY_ERROR_TITLE'),
                    <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_DELETION_ERROR_MESSAGE', {
                        errorMessage: error.message,
                    }),
                );
                return throwError(() => error);
            }),
            takeUntilDestroyed(this.destroyRef),
        );
    }

    /**
     * Checks different conditions for a valid PI schedule.
     *
     * @param schedule physical inventory schedule
     */
    private checkAndEmitActivePISchedule(schedule: PiScheduleDetails | undefined): void {
        const isPiActive =
            isDefined(schedule) &&
            schedule.startDateAndTime.isValid() &&
            schedule.endDateAndTime.isValid() &&
            !schedule.isPISubmitted &&
            !schedule.isRescheduled;
        this.activePIScheduleSubject.next(isPiActive);
    }

    /**
     * Sets a timer for an hour for offline mode because there is no sync triggered in offline mode.
     * The timer will be cleared if the user confirms the alert or goes online.
     */
    public setOfflineAlertTimer(): void {
        this.checkAndClearOldOfflineAlertTimer();

        this.isOfflineAlertTimerActive = true;
        this.offlineAlertTimerId = window.setTimeout(
            () => this.activePIScheduleSubject.next(true),
            ConfigConstants.oneHourInMilliSec,
        );
    }

    /**
     * Clears the timer for offline mode.
     * This is called when the user confirms the alert or goes online.
     * The timer will be set again if the user goes offline.
     */
    public checkAndClearOldOfflineAlertTimer(): void {
        if (this.isOfflineAlertTimerActive) {
            window.clearTimeout(this.offlineAlertTimerId);
            this.isOfflineAlertTimerActive = false;
        }
    }

    /**
     * Stops both scheduled alert timers. Called when the device lock is released: the schedule
     * belongs to a PACE session that is no longer active on this device, so neither alert may
     * still fire. `init` sets them up again once the lock is acquired here.
     */
    public clearScheduledTimers(): void {
        this.checkAndClearOldOfflineAlertTimer();

        if (isDefined(this.physicalInventoryTimerId)) {
            window.clearTimeout(this.physicalInventoryTimerId);
            this.physicalInventoryTimerId = undefined;
        }
    }

    /**
     * Sets a timer to show the Physical Inventory time as soon as the schedule time is reached.
     *
     * @param scheduleTime time at which the physical inventory will be triggered.
     */
    public setPhysicalInventoryTimer(scheduleTime: moment.Moment): void {
        const now = moment();

        // no need to set a timer if the schedule time is already passed, as this will be handled in the pi-alert component
        if (scheduleTime.isBefore(now)) {
            return;
        }

        const duration = moment.duration(scheduleTime.diff(now));
        const delay = duration.asMilliseconds();

        this.physicalInventoryTimerId = window.setTimeout(() => {
            // when the timeout hits, make sure the device is locked in before showing the PI form
            this.deviceLockService
                .refreshState()
                .pipe(
                    take(1),
                    tap((state) => {
                        if (state.ownedByMe) {
                            this.activePIScheduleSubject.next(true);
                        }
                    }),
                )
                .subscribe();
        }, delay);
    }

    /**
     * Gets the physical inventory data from local, if not available, then gets it from the backend.
     *
     * @returns Observable<PhysicalInventoryModel | undefined>
     */
    public getPhysicalInventoryData(): Observable<PhysicalInventoryModel | undefined> {
        return this.physicalInventoryStorageService.getPIData().pipe(
            mergeMap((data) => {
                if (!isDefined(data)) {
                    return this.getPhysicalInventoryFromBackend();
                }
                return of(new PhysicalInventoryModelFromJson(data));
            }),
        );
    }

    /**
     * Gets the physical inventory data from the backend.
     * If the data is not found in the backend then returns undefined.
     *
     * @returns Observable<PhysicalInventoryModel | undefined>
     */
    public getPhysicalInventoryFromBackend(): Observable<PhysicalInventoryModel | undefined> {
        return this.odataPhysicalInventoryService.getPhysicalInventory().pipe(
            mergeMap((data: PhysicalInventoryModel) => {
                this.log.debug(`${this.constructor.name}: updating physical inventory with new data from backend`);
                return this.physicalInventoryStorageService.savePIData(data);
            }),
            catchError((err) => {
                this.log.error(`${this.constructor.name}: error fetching physical inventory data from backend`, err);
                // As requested by Raju, Gateway will always provide a valid response to the user.
                // Even if there is no data for Physical Inventory, GW is supposed to send an empty response.
                // So, the only scenario where we can get an error is if the user is offline.
                // Even in this case, as requested by Jurgen,
                // we need to display an alert to user informing to compete the PI once they are online.
                // So, returning undefined to handle it as a valid scenario.
                return of(undefined);
            }),
        );
    }

    /**
     * Updates the PI schedule details in storage and triggers the activePISchedule$ observable to emit a value.
     *
     * @param alertType type of alert to be shown to the user.
     * @returns Observable<void>
     */
    public updatePIScheduleDetails(alertType: PI_ALERT_TYPE): Observable<void> {
        return this.physicalInventoryStorageService.getPIScheduleDetails().pipe(
            filter(isDefined),
            mergeMap((data) => {
                const newData = {
                    ...data,
                    [alertType]: true,
                    isRescheduled: alertType === PI_ALERT_TYPE.RESCHEDULE_ALERT,
                };
                return this.physicalInventoryStorageService.savePIScheduleDetails(newData);
            }),
            mergeMap(() => {
                if (alertType === PI_ALERT_TYPE.RESCHEDULE_ALERT) {
                    return this.physicalInventoryStorageService
                        .deletePIData()
                        .pipe(tap(() => this.activePIScheduleSubject.next(false)));
                }
                return of(undefined);
            }),
        );
    }

    public compareAndUpdateLatestPIScheduleToLocal(
        originalSchedule: PiScheduleDetails | undefined,
        newStartDateTime: moment.Moment,
        newEndDateTime: moment.Moment,
    ): Observable<PiScheduleDetails> {
        const isExisting = isDefined(originalSchedule);
        const hasScheduleChanged =
            isExisting &&
            (!originalSchedule.startDateAndTime.isSame(newStartDateTime) ||
                !originalSchedule.endDateAndTime.isSame(newEndDateTime));

        if (hasScheduleChanged || !isExisting) {
            const newSchedule = this.setPIScheduleDetails(newStartDateTime, newEndDateTime);
            return this.physicalInventoryStorageService.savePIScheduleDetails(newSchedule).pipe(
                mergeMap(() => this.physicalInventoryStorageService.deletePIData()),
                map(() => newSchedule),
            );
        } else {
            return of(originalSchedule);
        }
    }

    /**
     * Submits the physical inventory to the backend.
     * If the physical inventory is already submitted remotely, then throws an error.
     * If the physical inventory is successfully submitted, then saves the schedule details to the storage.
     *
     * @param model PhysicalInventoryModel
     * @returns Observable<void>
     */
    public submitPhysicalInventory(model: PhysicalInventoryModel): Observable<void> {
        return this.physicalInventoryStorageService.getPIScheduleDetails().pipe(
            filter(isDefined),
            switchMap((piSchedule) => {
                // if PI was already submitted remotely,
                // then no need to send it to backend again
                if (piSchedule.isPISubmitted) {
                    this.log.debug(`${this.constructor.name}: physical inventory is already submitted`);
                    this.activePIScheduleSubject.next(false);
                    return of(undefined);
                }
                piSchedule.isPISubmitted = true;
                return this.odataPhysicalInventoryService.sendPhysicalInventory(model).pipe(
                    mergeMap(() => {
                        this.log.debug('RequestNotificationComponent -> sendOfflineData');
                        return this.synchronizationService.sendOfflineData();
                    }),
                    mergeMap(() => this.savePIStatusOnSubmit(piSchedule)),
                );
            }),
            catchError((error) => {
                this.log.error(`${this.constructor.name}: Error while submitting physical inventory:`, error);
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('PHYSICAL_INVENTORY_FAILED_TITLE'),
                    <string>(
                        this.translate.instant('PHYSICAL_INVENTORY_FAILED_MESSAGE', { errorMessage: error.message })
                    ),
                );
                return throwError(() => error);
            }),
        );
    }

    /**
     * Saves physical inventory status in storage after successful submission to the backend.
     *
     * @param schedule physical inventory schedule
     * @returns Observable<void>
     */
    public savePIStatusOnSubmit(schedule: PiScheduleDetails): Observable<void> {
        this.log.debug(
            `${this.constructor.name}: successfully submitted physical inventory. Saving schedule details to storage.`,
        );

        // Added a guard to ensure the schedule is submitted
        // before saving it and clearing the physical inventory data.
        if (!schedule.isPISubmitted) {
            return of(undefined);
        }
        return this.physicalInventoryStorageService.savePIScheduleDetails(schedule).pipe(
            mergeMap(() => this.physicalInventoryStorageService.deletePIData()),
            tap(() => this.activePIScheduleSubject.next(false)),
        );
    }

    /**
     * Resets the PI schedule details to initial values.
     *
     * @param startDateTime start date and time of schedule
     * @param endDateTime end date and time of schedule
     * @returns full schedule state
     */
    private setPIScheduleDetails(startDateTime?: moment.Moment, endDateTime?: moment.Moment): PiScheduleDetails {
        return {
            isScheduleAlertConfirmed: false,
            startDateAndTime: startDateTime ?? moment.invalid(),
            endDateAndTime: endDateTime ?? moment.invalid(),
            isPISubmitted: false,
            isOfflineAlertConfirmed: false,
            isRescheduleAlertConfirmed: false,
            isNewScheduleAlertConfirmed: false,
            isRescheduled: false,
        };
    }
}
